import React from 'react';
import TableHeader from './TableHeader';
import TableBody from './TableBody';
import TablePagination from './TablePagination';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const DataTable = ({
  config,
  tableData,
  onView,
  onEdit,
  onDelete,
  onReport,
  onExport,
  onDeleteSelected,
  onStatusCheck
}) => {
  const {
    loading,
    error,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    sortField,
    sortDirection,
    handleSort,
    selectedRows,
    handleRowSelect,
    handleSelectAll,
    filters,
    handleFilterChange,
    paginatedData,
    sortedData,
    totalPages,
    data
  } = tableData;

  if (loading) {
    return <LoadingState message={`Loading ${config.title.toLowerCase()}...`} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={() => window.location.reload()} />;
  }
  
  const handleMasterExport = (type) => {
    try {
      // Use selected rows OR full filtered data
      let exportData = [];
  
      if (selectedRows.length > 0) {
        exportData = data.filter(item =>
          selectedRows.includes(item.id || item[config.idField])
        );
      } else {
        exportData = sortedData; // export full filtered data
      }
  
      if (!exportData || exportData.length === 0) {
        alert("No data available to export");
        return;
      }
  
      // 👉 Only export visible columns
      const formattedData = exportData.map(row => {
        const obj = {};
        config.columns.forEach(col => {
          obj[col.label] = row[col.key];
        });
        return obj;
      });
  
      if (type === "excel") {
        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, config.title || "Data");
  
        const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buffer]), `${config.title || "data"}.xlsx`);
      }
  
      if (type === "csv") {
        const ws = XLSX.utils.json_to_sheet(formattedData);
        const csv = XLSX.utils.sheet_to_csv(ws);
        saveAs(new Blob([csv]), `${config.title || "data"}.csv`);
      }
  
      if (type === "copy") {
        const text = JSON.stringify(formattedData, null, 2);
        navigator.clipboard.writeText(text);
        alert("Copied to clipboard!");
      }
  
    } catch (err) {
      console.error("Export error:", err);
      alert("Export failed");
    }
  };

  // Get unique values for filter options
  const getFilterOptions = (key) => {
    if (!Array.isArray(data)) return [];
    const options = data
      .map(item => item[key])
      .filter(value => value !== null && value !== undefined);
    return [...new Set(options)];
  };
  

  return (
    <div className="card">
      <TableHeader
        config={config}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        handleFilterChange={handleFilterChange}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        setCurrentPage={setCurrentPage}
        totalRecords={sortedData.length}
        getFilterOptions={getFilterOptions}
        selectedRows={selectedRows}
        onExport={handleMasterExport}
        onDeleteSelected={onDeleteSelected}
      />

      <TableBody
        config={config}
        data={paginatedData}
        sortField={sortField}
        sortDirection={sortDirection}
        handleSort={handleSort}
        selectedRows={selectedRows}
        handleRowSelect={handleRowSelect}
        handleSelectAll={handleSelectAll}
        onView={onView}
        onEdit={onEdit}
        onReport={onReport}
        onDelete={onDelete}
        onStatusCheck = {onStatusCheck}
      />

      <TablePagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalRecords={sortedData.length}
      />
    </div>
  );
};

export default DataTable;